"use client";

import { BsSearch } from "react-icons/bs";
import Link from "next/link";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaHome, FaMoon, FaSun } from "react-icons/fa";
import { BsThreeDots } from "react-icons/bs";
import { TbUser } from "react-icons/tb";
import { usePathname } from "next/navigation";

import { ArticleCategoryType } from "@/types/article";
import { AuthContext } from "@/components/providers/AuthContextProvider";
import UserDropdown from "./UserDropdown";
import { useTheme } from "@/components/providers/ThemeProvider";
import { SocialLinkType } from "@/types/common";
import { getDisplayName, getLabel } from "@/data/labels";
import { getProfileImageUrl } from "@/utils/getImageUrl";
import { getLocaleFromPathname, getLocalePrefix } from "@/utils/getLocaleFromPath";
import { CgMenuGridO } from "react-icons/cg";

interface HeaderMainNavigationProps {
  mainNavLinks: ArticleCategoryType[];
  categories: ArticleCategoryType[];
  searchModalOpen: boolean;
  setSearchModalOpen: Dispatch<SetStateAction<boolean>>;
  setHamburgerMenuOpen: Dispatch<SetStateAction<boolean>>;

  socialLinks: SocialLinkType[];
}

export default function HeaderMainNavigation({
  mainNavLinks,
  categories,
  searchModalOpen,
  setSearchModalOpen,
  setHamburgerMenuOpen,
  socialLinks,
}: HeaderMainNavigationProps) {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const hiddenItemRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setMoreDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { darkMode, toggleDarkMode } = useTheme();
  const authContext = useContext(AuthContext);

  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const localePrefix = getLocalePrefix(pathname);
  const language = locale?.split("-")[0] || "te";

  // Use mainNavLinks (configured in TopBar) for the top navigation bar
  const allNavLinks = mainNavLinks?.length > 0 ? [...mainNavLinks] : [...(categories || [])];
  const displayCount = visibleCount > 0 ? visibleCount : allNavLinks.length;
  const visibleLinks = allNavLinks.slice(0, displayCount);
  const dropdownLinks = allNavLinks.slice(displayCount);

  const calculateVisibleItems = useCallback(() => {
    if (!navContainerRef.current || !hiddenItemRef.current || allNavLinks.length === 0) return;

    const containerWidth = navContainerRef.current.getBoundingClientRect().width;
    const gap = window.matchMedia("(min-width: 1024px)").matches ? 24 : 20;
    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < allNavLinks.length; i++) {
      const navLink = allNavLinks[i];
      hiddenItemRef.current.textContent =
        getDisplayName(navLink?.name, locale) || navLink?.slug || "";
      const itemWidth = hiddenItemRef.current.getBoundingClientRect().width;
      const neededWidth = (i === 0 ? 0 : gap) + itemWidth;

      if (totalWidth + neededWidth <= containerWidth) {
        totalWidth += neededWidth;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count);
  }, [allNavLinks, locale]);

  useEffect(() => {
    calculateVisibleItems();
    window.addEventListener("resize", calculateVisibleItems);
    return () => window.removeEventListener("resize", calculateVisibleItems);
  }, [calculateVisibleItems]);

  if (!authContext) {
    return null;
  }

  const { user, isUserLoading } = authContext;

  return (
    <div className="bg-blue-400 h-12 md:h-10 transition-all duration-500 text-white relative z-10">
      <div className="container flex items-center justify-between h-full gap-2">
        {/* Left: Fixed icons + Scrollable categories */}
        <div className="flex-1 min-w-0 flex items-center h-full gap-2 xl:gap-4 relative">
          {/* Fixed: Grid and Home icons */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              className="hidden text-xl text-white lg:block hover:text-black transition-colors"
              onClick={() => setHamburgerMenuOpen(true)}
              aria-label="Menu"
            >
              <CgMenuGridO size={22} />
            </button>
            <Link href="/">
              <button
                className="hidden text-xl text-white lg:block hover:text-black transition-colors"
                aria-label="Home"
              >
                <FaHome />
              </button>
            </Link>
          </div>

          {/* Nav Container */}
          <div ref={navContainerRef} className="relative flex-1 min-w-0 h-full flex items-center">
            {/* Categories */}
            <div className="h-full flex items-center gap-5 lg:gap-6 overflow-hidden px-2 shrink min-w-0">
              {/* Duplicate with TopBar "Latest News" — keep commented
              <Link
                href={`${localePrefix}/all-news`}
                className="text-xl font-semibold uppercase whitespace-nowrap hover:text-black transition-colors shrink-0"
                aria-label={`Go to latest news page`}
              >
                {getLabel(language, "LATEST_NEWS")}
              </Link>
              */}
              {visibleLinks?.map((navLink, index) => {
                const isTag = (navLink as { linkType?: string })?.linkType === "tag";
                const href = isTag
                  ? `${localePrefix}/topics/tag/${navLink?.slug}`
                  : `${localePrefix}/${navLink?.slug}`;
                return (
                  <Link
                    href={href}
                    key={index}
                    className="text-xl font-semibold uppercase whitespace-nowrap hover:text-black transition-colors shrink-0"
                    aria-label={`Go to ${getDisplayName(navLink?.name, locale)} page`}
                  >
                    {getDisplayName(navLink?.name, locale)}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* More categories, theme, search, and Register/Profile */}
        <div className="flex items-center h-full gap-3 sm:gap-6 shrink-0">
          {dropdownLinks.length > 0 && (
            <div className="relative h-full flex items-center shrink-0" ref={dropdownRef}>
              <button
                onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
                className="text-white hover:text-black transition-colors h-full px-1"
                aria-label="More categories"
              >
                <BsThreeDots size={20} />
              </button>

              {moreDropdownOpen && (
                <div className="absolute top-[100%] right-0 mt-1 w-48 max-h-[11rem] overflow-y-auto overscroll-y-contain bg-white dark:bg-[#1a1a1a] rounded shadow-lg border border-gray-200 dark:border-gray-800 py-2 z-50">
                  {dropdownLinks.map((navLink, index) => {
                    const isTag = (navLink as { linkType?: string })?.linkType === "tag";
                    const href = isTag
                      ? `${localePrefix}/topics/tag/${navLink?.slug}`
                      : `${localePrefix}/${navLink?.slug}`;
                    return (
                      <Link
                        href={href}
                        key={`dropdown-${index}`}
                        onClick={() => setMoreDropdownOpen(false)}
                        className="block px-4 py-2 text-lg text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-primaryColor transition-colors"
                        aria-label={`Go to ${getDisplayName(navLink?.name, locale)} page`}
                      >
                        {getDisplayName(navLink?.name, locale)}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <button
            className="hidden text-lg text-white sm:block hover:text-black transition-colors"
            onClick={toggleDarkMode}
            aria-label="Toggle Theme"
          >
            {darkMode ? <FaSun /> : <FaMoon />}
          </button>

          <button
            className="text-lg text-white hover:text-black transition-colors"
            onClick={() => setSearchModalOpen(true)}
            aria-label="Search"
          >
            <BsSearch strokeWidth={"0.3"} />
          </button>
          {isUserLoading ? (
            <span className="w-20 h-7 rounded-full bg-white/10 animate-pulse" aria-hidden />
          ) : !user ? (
            <Link href={`${localePrefix}/register`} aria-label="Go to register page">
              <button
                className="bg-[#E80D40] text-white rounded-full px-3 text-[12px] h-[28px] font-[600] hover:bg-[#c00a35] transition-colors"
                aria-label={getLabel(language, "REGISTER")}
              >
                {getLabel(language, "REGISTER")}
              </button>
            </Link>
          ) : (
            <div className="relative">
              <button
                type="button"
                onClick={() => setUserDropdownOpen(true)}
                className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden cursor-pointer text-white border border-white/30 hover:bg-white/10 transition-colors shrink-0"
                aria-label={getLabel(language, "PROFILE")}
              >
                {user?.pictureURL ? (
                  <img
                    key={user.pictureURL}
                    src={getProfileImageUrl(user.pictureURL) || user.pictureURL || ""}
                    alt=""
                    className="object-cover w-full h-full"
                    onError={e => {
                      e.currentTarget.src = "/images/avatar.webp";
                    }}
                  />
                ) : (
                  <span className="flex items-center justify-center w-full h-full bg-white/10">
                    <TbUser className="w-5 h-5" aria-hidden />
                  </span>
                )}
              </button>

              {userDropdownOpen && <UserDropdown setUserDropdownOpen={setUserDropdownOpen} />}
            </div>
          )}
        </div>
      </div>

      <span
        ref={hiddenItemRef}
        className="absolute invisible text-xl font-semibold uppercase whitespace-nowrap"
        aria-hidden
      />
    </div>
  );
}
